import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useLang } from '../util/lang';
import SHADE_IMAGES from './shadeImages';
import './AdditionalSyringeSelector.css';
import { Typography } from '@material-ui/core';

const SHADE_GROUPS = [
    {
        nameKey: '@PatientInfo.smileDesign.restorativePhase.standardShades',
        guides: [
            { nameKey: '@PatientInfo.smileDesign.restorativePhase.gcBleachShadeGuide', shades: ['XBW', 'BW'] },
            { nameKey: '@PatientInfo.smileDesign.restorativePhase.vitaClassicShadeGuide', shades: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2'] },
        ],
    },
    { nameKey: '@PatientInfo.smileDesign.restorativePhase.opaqueShades', guides: [{ shades: ['AO1', 'AO2', 'AO3'] }] },
    { nameKey: '@PatientInfo.smileDesign.restorativePhase.translucentShades', guides: [{ shades: ['JE', 'AE'] }] },
];

/** Renders one additional syringe choice and its conditional quantity control. */
const SyringeOption = ({ value, label = value, quantity, onToggle, onQuantityChange }) => {
    const isSelected = quantity > 0;

    /** Toggles the selected syringe option. */
    const handleToggle = () => onToggle(value);

    /** Stops quantity controls from toggling the syringe option. */
    const stopToggle = (event) => event.stopPropagation();

    return (
        <div
            className={`additional-syringe-selector__option${isSelected ? ' additional-syringe-selector__option--selected' : ''}`}
            onClick={handleToggle}
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
        >
            <img src={SHADE_IMAGES[value]} alt={label} />
            <span>{label}</span>
            {isSelected && (
                <div className="additional-syringe-selector__quantity" onClick={stopToggle}>
                    <button type="button" disabled={quantity === 1} onClick={() => onQuantityChange(value, -1)}>
                        −
                    </button>
                    <strong>{quantity}</strong>
                    <button type="button" onClick={() => onQuantityChange(value, 1)}>
                        +
                    </button>
                </div>
            )}
        </div>
    );
};

/** Renders all optional additional syringe choices with quantity controls. */
const AdditionalSyringeSelector = ({ name, title }) => {
    const get = useLang();
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            render={({ field }) => {
                const selectedSyringes = Array.isArray(field.value) ? field.value : [];

                /** Returns the selected quantity for one syringe option. */
                const getQuantity = (value) => selectedSyringes.find((item) => item.option === value)?.quantity || 0;

                /** Adds or removes an optional syringe with a default quantity of one. */
                const toggleSyringe = (value) => {
                    field.onChange(
                        getQuantity(value) > 0
                            ? selectedSyringes.filter((item) => item.option !== value)
                            : [...selectedSyringes, { option: value, quantity: 1 }]
                    );
                };

                /** Changes a selected syringe quantity without allowing values below one. */
                const changeQuantity = (value, amount) => {
                    field.onChange(
                        selectedSyringes.map((item) =>
                            item.option === value ? { ...item, quantity: Math.max(1, item.quantity + amount) } : item
                        )
                    );
                };

                return (
                    <section className="additional-syringe-selector">
                        <div className="restorative-question-title">{title}</div>
                        <div className="additional-syringe-selector__group">
                            <h4>{get('@PatientInfo.smileDesign.restorativePhase.options.maskingLiner')}</h4>
                            <SyringeOption
                                value="Masking Liner"
                                label={get('@PatientInfo.smileDesign.restorativePhase.maskingLinerMl')}
                                quantity={getQuantity('Masking Liner')}
                                onToggle={toggleSyringe}
                                onQuantityChange={changeQuantity}
                            />
                        </div>
                        {SHADE_GROUPS.map((group) => (
                            <div className="additional-syringe-selector__group" key={group.nameKey}>
                                <Typography variant="h4" style={{fontWeight: 'bold'}}>{get(group.nameKey)}</Typography>
                                <div className="additional-syringe-selector__guides">
                                    {group.guides.map((guide) => (
                                        <div className="additional-syringe-selector__guide" key={guide.nameKey || group.nameKey}>
                                            {guide.nameKey && <div className="additional-syringe-selector__guide-title">{get(guide.nameKey)}</div>}
                                            <div className="additional-syringe-selector__options">
                                                {guide.shades.map((shade) => (
                                                    <SyringeOption
                                                        key={shade}
                                                        value={shade}
                                                        quantity={getQuantity(shade)}
                                                        onToggle={toggleSyringe}
                                                        onQuantityChange={changeQuantity}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                );
            }}
        />
    );
};

export default AdditionalSyringeSelector;
